import React, { useMemo } from 'react';
import { motion, useScroll, useTransform, useMotionValue, useSpring } from 'framer-motion';
import { useScrollAnimation, fadeInUp, staggerContainer } from './utils/scrollAnimations';
import { FlippingCard } from '../../../../components/ui/flipping-card';

const VisionSection = () => {
  const { ref, isInView } = useScrollAnimation({ threshold: 0.15 });
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [0, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.9, 1, 0.9]);

  // Mouse tracking for interactive effects - optimized
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springConfig = { damping: 50, stiffness: 150 }; // Increased damping for smoother motion
  const x = useSpring(useTransform(mouseX, (latest) => latest / 20), springConfig);
  const yMouse = useSpring(useTransform(mouseY, (latest) => latest / 20), springConfig);

  // Memoized floating particles - reduced count
  const floatingParticles = useMemo(() => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const particleCount = isMobile ? 3 : 8; // Reduced from 15 to 3:8
    return Array.from({ length: particleCount }, (_, i) => ({
      id: i,
      size: Math.random() * 4 + 2, // Smaller particles
      duration: Math.random() * 25 + 20, // Slower animations
      delay: Math.random() * 10,
      x: Math.random() * 100,
      y: Math.random() * 100,
    }));
  }, []);

  const handleMouseMove = React.useCallback((e) => {
    // Only update on desktop
    if (typeof window !== 'undefined' && window.innerWidth < 1024) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    mouseX.set(e.clientX - rect.left - rect.width / 2);
    mouseY.set(e.clientY - rect.top - rect.height / 2);
  }, [mouseX, mouseY]);

  const features = [
    {
      id: 'personalized-learning',
      title: 'Kişiselleştirilmiş Öğrenme',
      shortDescription: 'Her öğrenciye özel, kendi hızında ilerleyen dinamik eğitim.',
      description: 'Yapay zeka destekli öğrenme patikalarıyla her öğrenciye özel, kendi hızında ilerleyen dinamik bir eğitim deneyimi sunuyoruz. Öğrenme stillerine göre adapte olan içerikler ve gerçek zamanlı geri bildirimlerle başarıyı artırıyoruz.',
      icon: '🎯',
      gradient: 'from-blue-500 to-cyan-500',
      color: '#3b82f6',
      imageSrc: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=400&h=300&fit=crop',
      imageAlt: 'Kişiselleştirilmiş Öğrenme',
      buttonText: 'Keşfet'
    },
    {
      id: 'productivity-focused',
      title: 'Üretkenlik Odaklı',
      shortDescription: 'Gerçek projelerle öğrenme portföyünüzü oluşturun.',
      description: 'Sadece öğrenmek değil, üretmek için tasarlandı. Gerçek projelerle öğrenme portföyünüzü oluşturun, pratik deneyim kazanın. Scratch, Python, Web Geliştirme ve daha fazlasıyla kendi projelerinizi hayata geçirin.',
      icon: '🚀',
      gradient: 'from-purple-500 to-pink-500',
      color: '#a855f7',
      imageSrc: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=300&fit=crop',
      imageAlt: 'Üretkenlik Odaklı',
      buttonText: 'Başla'
    },
    {
      id: 'quality-assurance',
      title: 'Kalite Güvencesi',
      shortDescription: 'Uzman eğitimciler ve pedagoji araştırmalarıyla desteklenen içerikler.',
      description: 'Uzman eğitimciler ve pedagoji araştırmalarıyla desteklenen, titizlikle hazırlanmış ve sürekli güncellenen içerikler. 1800+ ders içeriği ile kapsamlı bir eğitim deneyimi sunuyoruz.',
      icon: '✨',
      gradient: 'from-yellow-500 to-orange-500',
      color: '#f59e0b',
      imageSrc: 'https://images.unsplash.com/photo-1509062522246-3755977927d7?w=400&h=300&fit=crop',
      imageAlt: 'Kalite Güvencesi',
      buttonText: 'İncele'
    },
    {
      id: 'community-support',
      title: 'Topluluk Desteği',
      shortDescription: 'Hub\'da projelerinizi paylaşın, akranlarınızdan öğrenin.',
      description: 'Hub\'da projelerinizi paylaşın, akranlarınızdan öğrenin, başarılarınızı rozetlerle kutlayın. Birlikte büyüyen bir öğrenme ekosistemi. 50K+ aktif öğrenci ile güçlü bir topluluk.',
      icon: '👥',
      gradient: 'from-green-500 to-emerald-500',
      color: '#10b981',
      imageSrc: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&h=300&fit=crop',
      imageAlt: 'Topluluk Desteği',
      buttonText: 'Katıl'
    }
  ];

  return (
    <section 
      id="vision"
      ref={ref}
      className="relative py-20 md:py-20 bg-gradient-to-b from-white via-light-purple/30 to-white overflow-hidden"
      onMouseMove={handleMouseMove}
    >
      {/* Optimized Floating Orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        {[...Array(3)].map((_, i) => {
          const randomSize = Math.random() * 200 + 150;
          const randomX = Math.random() * 100;
          const randomY = Math.random() * 100;
          const randomMoveX = Math.random() * 200 - 100;
          const randomMoveY = Math.random() * 200 - 100;
          const randomDuration = Math.random() * 25 + 20;
          const randomOpacity = Math.random() * 0.2 + 0.1;
          
          return (
            <motion.div
              key={`orb-${i}`}
              className="absolute rounded-full"
              style={{
                width: randomSize,
                height: randomSize,
                left: `${randomX}%`,
                top: `${randomY}%`,
              }}
              animate={{
                x: [0, randomMoveX, 0],
                y: [0, randomMoveY, 0],
                scale: [1, 1.2, 1],
                opacity: [0.05, randomOpacity, 0.05],
              }}
              transition={{
                duration: randomDuration,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 1,
              }}
            >
              <div 
                className="w-full h-full rounded-full"
                style={{
                  background: `radial-gradient(circle, 
                    rgba(108, 93, 211, ${randomOpacity}) 0%, 
                    rgba(139, 92, 246, ${randomOpacity * 0.5}) 50%, 
                    transparent 100%)`,
                  filter: 'blur(60px)',
                }}
              />
            </motion.div>
          );
        })}
      </div>

      {/* Optimized Floating Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        {floatingParticles.map((particle) => (
          <motion.div
            key={particle.id}
            className="absolute rounded-full"
            style={{
              width: particle.size,
              height: particle.size,
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              background: `radial-gradient(circle, rgba(108, 93, 211, 0.3) 0%, transparent 70%)`,
            }}
            animate={{
              y: [0, -25, 0],
              x: [0, Math.random() * 15 - 7, 0],
              opacity: [0.2, 0.6, 0.2],
              scale: [1, 1.3, 1],
            }}
            transition={{
              duration: particle.duration,
              repeat: Infinity,
              ease: "easeInOut",
              delay: particle.delay,
            }}
          />
        ))}
      </div>

      {/* Enhanced Animated Background with Mouse Tracking */}
      <motion.div
        style={{ opacity, scale, x, y: yMouse }}
        className="absolute inset-0 pointer-events-none"
      >
        <motion.div 
          className="absolute top-0 left-1/4 w-[700px] h-[700px] bg-gradient-radial from-primary/15 to-transparent rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute bottom-0 right-1/4 w-[700px] h-[700px] bg-gradient-radial from-purple-500/15 to-transparent rounded-full blur-3xl"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-primary/8 to-transparent rounded-full blur-3xl"
          animate={{
            scale: [1, 1.4, 1],
            opacity: [0.06, 0.15, 0.06],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial="initial"
          animate={isInView ? "animate" : "initial"}
          variants={fadeInUp}
          className="text-center mb-24"
        >
          <motion.div
            className="inline-block mb-8"
            whileHover={{ scale: 1.1, rotate: [0, -2, 2, 0] }}
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <motion.span 
              className="px-6 py-3 bg-gradient-to-r from-primary/20 via-purple-500/20 to-primary/20 backdrop-blur-xl text-primary font-bold rounded-full text-sm uppercase tracking-wider shadow-2xl border border-primary/30"
              animate={{
                boxShadow: [
                  '0 10px 30px rgba(108, 93, 211, 0.2)',
                  '0 15px 40px rgba(108, 93, 211, 0.4)',
                  '0 10px 30px rgba(108, 93, 211, 0.2)',
                ],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              Platform Özellikleri
            </motion.span>
          </motion.div>
          <motion.h2
            className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold mb-8 leading-tight"
            initial={{ opacity: 0, y: 40, scale: 0.9 }}
            animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
            transition={{ delay: 0.3, type: "spring", stiffness: 100 }}
            style={{
              transformStyle: 'preserve-3d',
            }}
          >
            <motion.span 
              className="text-dark relative inline-block"
              whileHover={{ scale: 1.05, rotateY: 5 }}
            >
              Geleceğin
            </motion.span>{' '}
            <motion.span 
              className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-purple-600 via-pink-500 to-primary relative inline-block"
              animate={{
                backgroundPosition: ['0%', '100%', '0%'],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "linear"
              }}
              style={{
                backgroundSize: '200% auto',
                filter: 'drop-shadow(0 4px 20px rgba(108, 93, 211, 0.4))',
              }}
              whileHover={{ scale: 1.05, rotateY: -5 }}
            >
              Eğitim Platformu
              {/* Holographic shimmer */}
              <motion.div
                className="absolute -inset-2 bg-gradient-to-r from-transparent via-white/40 to-transparent blur-xl"
                animate={{
                  x: ['-200%', '200%'],
                  opacity: [0, 0.6, 0],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1
                }}
              />
            </motion.span>
          </motion.h2>
          <motion.p
            className="text-base md:text-lg lg:text-xl xl:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed font-medium"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.5, type: "spring" }}
          >
            K-12 seviyesinde yapay zeka destekli eğitimde öncü.{' '}
            <motion.span 
              className="text-primary font-bold relative inline-block"
              whileHover={{ scale: 1.05 }}
            >
              <span className="relative z-10">Öğrencileri 21. yüzyıl becerileri</span>
              <motion.span
                className="absolute bottom-0 left-0 right-0 h-2 bg-primary/20 -z-0"
                initial={{ scaleX: 0 }}
                whileHover={{ scaleX: 1 }}
                transition={{ duration: 0.3 }}
              />
            </motion.span>{' '}
            ile donatıyoruz.
          </motion.p>
        </motion.div>

        <motion.div
          initial="initial"
          animate={isInView ? "animate" : "initial"}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10"
        >
          {features.map((feature, index) => {
            // Front Content Component
            const FrontContent = () => (
              <div className="flex flex-col h-full w-full p-4">
                <div className="relative w-full h-48 mb-4 rounded-lg overflow-hidden">
                  <img
                    src={feature.imageSrc}
                    alt={feature.imageAlt}
                    className="w-full h-full object-cover"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-20`} />
                </div>
                <div className="flex-grow flex flex-col">
                  <h3 className={`text-xl font-extrabold mb-3 bg-gradient-to-r ${feature.gradient} bg-clip-text text-transparent`}>
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed font-medium">
                    {feature.shortDescription}
                  </p>
                </div>
              </div>
            );

            // Back Content Component
            const BackContent = () => (
              <div className="flex flex-col items-center justify-center h-full w-full p-6">
                <h3 className={`text-xl font-extrabold mb-4 text-center bg-gradient-to-r ${feature.gradient} bg-clip-text text-transparent`}>
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed text-center mb-6 font-medium">
                  {feature.description}
                </p>
                <button 
                  className={`bg-gradient-to-r ${feature.gradient} text-white px-6 py-3 rounded-xl font-bold text-sm shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105`}
                  onClick={() => window.location.href = '#vision'}
                >
                  {feature.buttonText}
                </button>
              </div>
            );

            return (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 50, rotateX: -20 }}
                animate={isInView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
                transition={{ 
                  delay: index * 0.15 + 0.5, 
                  duration: 0.8,
                  type: "spring",
                  stiffness: 100
                }}
                className="flex justify-center"
              >
                <FlippingCard
                  height={380}
                  width={300}
                  frontContent={<FrontContent />}
                  backContent={<BackContent />}
                  className="shadow-2xl border-2 border-white/50"
                />
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default VisionSection;
